import { useCallback, useEffect, useRef, useState } from 'react';
import {
  consolidateInvoices,
  importStatement,
  invoices as loadInvoices,
  payInvoice,
  providers as loadProviders,
  statementImports,
  type Invoice,
  type StatementImport,
  type StatementImportResult,
  type StatementOutcome,
} from './api';
import type { PaymentsKey, PaymentsTranslate } from './i18n';
import { readStatementFile } from './statement-file';
import {
  Button,
  Id,
  Moment,
  Money,
  Notice,
  StatusBadge,
  Table,
  type BadgeTone,
  TableSkeleton,
  Pagination,
  usePagination,
} from '../../shell/kit';

const STATUS_TONE: Record<Invoice['status'], BadgeTone> = {
  issued: 'brand',
  paid: 'ok',
  cancelled: 'neutral',
};

const OUTCOME_TONE: Record<StatementOutcome, BadgeTone> = {
  matched: 'ok',
  already_paid: 'neutral',
  amount_mismatch: 'warn',
  payer_mismatch: 'danger',
  not_found: 'warn',
};

export interface InvoicesTabProps {
  readonly t: PaymentsTranslate;
  readonly onError: (message: string) => void;
}

export function InvoicesTab({ t, onError }: InvoicesTabProps) {
  const [rows, setRows] = useState<readonly Invoice[]>([]);
  const [provider, setProvider] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const reload = useCallback(async (): Promise<void> => {
    setLoading(true);
    try {
      const [list, all] = await Promise.all([loadInvoices(), loadProviders()]);
      setRows(list);
      const payment = all.providers.filter((p) => p.kind !== 'fiscal').map((p) => p.code);
      setProvider((current) => current || (payment[0] ?? ''));
    } catch (err) {
      onError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [onError]);

  useEffect(() => {
    void reload();
  }, [reload]);

  // Скелет вместо строчки «Загрузка…»: держит высоту будущей таблицы, поэтому
  // страница не подпрыгивает в момент прихода данных.
  // Счета копятся месяцами — без страниц список растёт бесконечно.
  const paged = usePagination(rows);

  return (
    <section className="space-y-4">
      <StatementPanel t={t} onError={onError} onImported={reload} />
      <ConsolidatePanel t={t} onError={onError} onDone={reload} />

      <h3 className="text-lg font-semibold text-ink">{t('inv.title')}</h3>

      {loading ? (
            <TableSkeleton rows={5} columns={5} />
          ) : rows.length === 0 ? (
        <p className="text-ink-muted">{t('inv.empty')}</p>
      ) : (
        <div className="overflow-x-auto rounded border border-line bg-surface">
          <Table className="text-left">
            <thead className="bg-sunken text-xs uppercase text-ink-muted">
              <tr>
                <th className="px-3 py-2">{t('inv.number')}</th>
                <th className="px-3 py-2">{t('inv.customer')}</th>
                <th className="px-3 py-2">{t('inv.amount')}</th>
                <th className="px-3 py-2">{t('inv.due')}</th>
                <th className="px-3 py-2">{t('inv.status')}</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {paged.rows.map((row) => (
                <tr key={row.id} className="border-t border-line">
                  <td className="px-3 py-2 font-mono text-xs">{row.number}</td>
                  <td className="px-3 py-2 text-xs">
                    <Id value={row.customerId} />
                  </td>
                  <td className="px-3 py-2 font-medium">
                    <Money minor={row.amountMinor} />
                  </td>
                  <td className="px-3 py-2 text-xs">
                    {row.dueAt ? new Date(row.dueAt).toLocaleDateString() : '—'}
                  </td>
                  <td className="px-3 py-2">
                    <StatusBadge tone={STATUS_TONE[row.status]}>
                      {t(`inv.status.${row.status}` as PaymentsKey)}
                    </StatusBadge>
                  </td>
                  <td className="px-3 py-2 text-right">
                    {row.status === 'issued' && provider && (
                      <button
                        type="button"
                        disabled={busy}
                        className="text-xs text-ink-muted underline"
                        onClick={() => {
                          setBusy(true);
                          payInvoice(row.id, provider)
                            .then(reload)
                            .catch((err: Error) => onError(err.message))
                            .finally(() => setBusy(false));
                        }}
                      >
                        {t('inv.pay')}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
          <div className="px-3 pb-3">
            <Pagination paged={paged} />
          </div>
        </div>
      )}
    </section>
  );
}

/** «2026-09-05» → «05.09.2026» без перевода в Date: календарный день не должен сдвигаться поясом. */
function formatDay(iso: string): string {
  const [year, month, day] = iso.split('-');
  return year && month && day ? `${day}.${month}.${year}` : iso;
}

interface StatementPanelProps {
  readonly t: PaymentsTranslate;
  readonly onError: (message: string) => void;
  /** После разноски список счетов перечитывается: часть из них стала оплаченной. */
  readonly onImported: () => Promise<void>;
}

/**
 * Загрузка банковской выписки 1С. Файл читается в браузере и декодируется по
 * его же строке «Кодировка=» (обычно Windows-1251), на сервер уходит текст;
 * сервер сам сопоставляет платежи со счетами и возвращает итог по каждой строке.
 */
function StatementPanel({ t, onError, onImported }: StatementPanelProps) {
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<StatementImportResult | null>(null);
  const [history, setHistory] = useState<readonly StatementImport[]>([]);

  const loadHistory = useCallback(async (): Promise<void> => {
    try {
      setHistory(await statementImports());
    } catch (err) {
      onError((err as Error).message);
    }
  }, [onError]);

  useEffect(() => {
    void loadHistory();
  }, [loadHistory]);

  async function upload(file: File): Promise<void> {
    setBusy(true);
    try {
      const content = await readStatementFile(file);
      const imported = await importStatement(file.name, content);
      setResult(imported);
      await Promise.all([onImported(), loadHistory()]);
    } catch (err) {
      onError((err as Error).message);
    } finally {
      setBusy(false);
      if (input.current) input.current.value = '';
    }
  }

  return (
    <div className="space-y-3 rounded border border-line bg-surface p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="max-w-2xl">
          <h3 className="text-lg font-semibold text-ink">{t('stmt.title')}</h3>
          <p className="text-sm text-ink-muted">{t('stmt.hint')}</p>
        </div>
        <div>
          <input
            ref={input}
            type="file"
            accept=".txt,text/plain"
            className="hidden"
            aria-label={t('stmt.upload')}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void upload(file);
            }}
          />
          <Button tone="primary" disabled={busy} onClick={() => input.current?.click()}>
            {busy ? t('stmt.busy') : t('stmt.upload')}
          </Button>
        </div>
      </div>

      {result && (
        <div className="space-y-3">
          {result.replayed && <Notice tone="info">{t('stmt.replayed')}</Notice>}
          <Notice tone={result.import.counts.matched > 0 ? 'ok' : 'info'} title={result.import.fileName}>
            {t('stmt.summary', { ...result.import.counts })}
          </Notice>
          {result.lines.length === 0 ? (
            <p className="text-ink-muted">{t('stmt.empty')}</p>
          ) : (
            <div className="overflow-x-auto rounded border border-line">
              <Table className="text-left">
                <thead className="bg-sunken text-xs uppercase text-ink-muted">
                  <tr>
                    <th className="px-3 py-2">{t('stmt.doc')}</th>
                    <th className="px-3 py-2">{t('stmt.date')}</th>
                    <th className="px-3 py-2">{t('stmt.amount')}</th>
                    <th className="px-3 py-2">{t('stmt.payer')}</th>
                    <th className="px-3 py-2">{t('stmt.purpose')}</th>
                    <th className="px-3 py-2">{t('stmt.invoice')}</th>
                    <th className="px-3 py-2">{t('stmt.outcome')}</th>
                  </tr>
                </thead>
                <tbody>
                  {result.lines.map((line) => (
                    <tr key={`${line.date}:${line.docNumber}:${line.amountMinor}`} className="border-t border-line">
                      <td className="px-3 py-2 font-mono text-xs">{line.docNumber}</td>
                      <td className="px-3 py-2 text-xs">{formatDay(line.date)}</td>
                      <td className="px-3 py-2 font-medium">
                        <Money minor={line.amountMinor} />
                      </td>
                      <td className="px-3 py-2 text-xs">
                        <div>{line.payerName ?? '—'}</div>
                        {line.payerBin && <div className="font-mono text-ink-muted">{line.payerBin}</div>}
                      </td>
                      <td className="max-w-xs px-3 py-2 text-xs text-ink-muted">{line.purpose}</td>
                      <td className="px-3 py-2 font-mono text-xs">{line.invoiceNumber ?? '—'}</td>
                      <td className="px-3 py-2">
                        <StatusBadge tone={OUTCOME_TONE[line.outcome]}>
                          {t(`stmt.outcome.${line.outcome}` as PaymentsKey)}
                        </StatusBadge>
                        {line.repeated && (
                          <div className="mt-1 text-xs text-ink-muted">{t('stmt.repeated')}</div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </div>
      )}

      {history.length > 0 && (
        <details className="text-sm">
          <summary className="cursor-pointer text-ink-muted">{t('stmt.history')}</summary>
          <div className="mt-2 overflow-x-auto rounded border border-line">
            <Table className="text-left">
              <thead className="bg-sunken text-xs uppercase text-ink-muted">
                <tr>
                  <th className="px-3 py-2">{t('stmt.file')}</th>
                  <th className="px-3 py-2">{t('stmt.period')}</th>
                  <th className="px-3 py-2">{t('stmt.outcome')}</th>
                  <th className="px-3 py-2">{t('stmt.loadedAt')}</th>
                </tr>
              </thead>
              <tbody>
                {history.map((row) => (
                  <tr key={row.id} className="border-t border-line">
                    <td className="px-3 py-2 text-xs">{row.fileName}</td>
                    <td className="px-3 py-2 text-xs">
                      {row.periodFrom && row.periodTo
                        ? `${formatDay(row.periodFrom)} — ${formatDay(row.periodTo)}`
                        : '—'}
                    </td>
                    <td className="px-3 py-2 text-xs">{t('stmt.summary', { ...row.counts })}</td>
                    <td className="px-3 py-2 text-xs">
                      <Moment iso={row.createdAt} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </details>
      )}
    </div>
  );
}

function previousMonth(): string {
  const now = new Date(Date.now() + 5 * 3_600_000);
  const month = now.getUTCMonth();
  const year = month === 0 ? now.getUTCFullYear() - 1 : now.getUTCFullYear();
  return `${year}-${String(month === 0 ? 12 : month).padStart(2, '0')}`;
}

function ConsolidatePanel({
  t,
  onError,
  onDone,
}: {
  readonly t: PaymentsTranslate;
  readonly onError: (message: string) => void;
  readonly onDone: () => Promise<void>;
}) {
  const [period, setPeriod] = useState(previousMonth);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  return (
    <div className="rounded border border-line bg-surface p-4">
      <h3 className="font-semibold text-ink">{t('inv.consolidate.title')}</h3>
      <p className="mt-1 text-sm text-ink-muted">{t('inv.consolidate.hint')}</p>
      <div className="mt-3 flex flex-wrap items-end gap-2">
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-ink-muted">{t('inv.consolidate.month')}</span>
          <input
            type="month"
            className="rounded border border-line bg-surface px-2 py-1 text-ink"
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
          />
        </label>
        <Button
          disabled={busy || !/^\d{4}-\d{2}$/.test(period)}
          onClick={() => {
            setBusy(true);
            setResult(null);
            consolidateInvoices(period)
              .then(async (built) => {
                setResult(t('inv.consolidate.done', { count: built.invoices.length }));
                await onDone();
              })
              .catch((err: Error) => onError(err.message))
              .finally(() => setBusy(false));
          }}
        >
          {t('inv.consolidate.run')}
        </Button>
        {result ? <span className="text-sm text-ink-muted">{result}</span> : null}
      </div>
    </div>
  );
}
